import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CirclePause,
  Loader2,
  RotateCcw,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { LightboxImageTrigger, LightboxProvider } from "@/modern/components/ui/lightbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import type { TemporalPhaseTag } from "@/react/components/marketing/temporal";
import {
  AiObservationsCard,
  AiWarningsCard,
} from "@/react/components/entityViews/files/components/editors/FileDetailModal/components/promote/components/AiInsightOverlay";
import type { PromoteSurfaceAsset, SurfaceSolutionTag, SurfaceWriteAction } from "../api/types";
import { getAvailableActions, skipActionLabel } from "../utils/actionMatrix";
import {
  getAcceptReadiness,
  initialAcceptFieldsFromAssets,
} from "../utils/mergeTagIds";
import PromoteSurfaceAcceptFields, { type ContentRoleTag } from "./PromoteSurfaceAcceptFields";
import PromoteSurfaceAiProposal from "./PromoteSurfaceAiProposal";

type Props = {
  open: boolean;
  assets: PromoteSurfaceAsset[];
  index: number;
  tags: SurfaceSolutionTag[];
  temporalTags: TemporalPhaseTag[];
  contentRoleTags?: ContentRoleTag[];
  boardUserAvailable?: boolean;
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  onIndexChange: (index: number) => void;
  onAccept: (
    asset: PromoteSurfaceAsset,
    solutionTagIds: number[],
    extras?: {
      caption?: string | null;
      alt_text?: string | null;
      temporalTagId?: number | null;
      contentRoleIds?: number[];
    }
  ) => void | Promise<void>;
  onAction: (
    action: Exclude<SurfaceWriteAction, "accept">,
    asset: PromoteSurfaceAsset
  ) => void | Promise<void>;
};

type FlashKind = "accept" | "reject" | "skip" | "reprocess";

const ACTION_FLASH: Record<
  FlashKind,
  { label: string; Icon: LucideIcon; iconWrap: string; labelClass: string }
> = {
  accept: {
    label: "Accepted",
    Icon: CheckCircle2,
    iconWrap: "bg-emerald-500 text-white shadow-lg shadow-emerald-900/30",
    labelClass: "text-white",
  },
  reject: {
    label: "Rejected",
    Icon: Ban,
    iconWrap: "bg-red-500 text-white shadow-lg shadow-red-900/30",
    labelClass: "text-white",
  },
  skip: {
    label: "Skipped",
    Icon: CirclePause,
    iconWrap: "bg-neutral-100 text-neutral-900 shadow-lg",
    labelClass: "text-white",
  },
  reprocess: {
    label: "Re-queued",
    Icon: RotateCcw,
    iconWrap: "bg-sky-500 text-white shadow-lg shadow-sky-900/30",
    labelClass: "text-white",
  },
};

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Wide two-pane review — image uses left space; AI + tags + actions stay visible on the right
 * without burying decisions behind scroll/collapsibles.
 */
export default function PromoteSurfaceReviewOverlay({
  open,
  assets,
  index,
  tags,
  temporalTags,
  contentRoleTags = [],
  boardUserAvailable = true,
  busy,
  onOpenChange,
  onIndexChange,
  onAccept,
  onAction,
}: Props) {
  const reduceMotion = useReducedMotion();
  const asset = assets[index];
  const [solutionTagIds, setSolutionTagIds] = useState<number[]>([]);
  const [suggestedSolutionTagIds, setSuggestedSolutionTagIds] = useState<number[]>([]);
  const [temporalTagId, setTemporalTagId] = useState<number | null>(null);
  const [contentRoleIds, setContentRoleIds] = useState<number[]>([]);
  const [caption, setCaption] = useState("");
  const [altText, setAltText] = useState("");
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [flashKind, setFlashKind] = useState<FlashKind | null>(null);
  const [localBusy, setLocalBusy] = useState(false);
  const [decideScrollEl, setDecideScrollEl] = useState<HTMLDivElement | null>(null);
  const [moreBelow, setMoreBelow] = useState(false);

  const contentRoleTagIds = useMemo(
    () => contentRoleTags.map((t) => t.id),
    [contentRoleTags]
  );

  useEffect(() => {
    if (!asset) return;
    setCaption(asset.caption ?? "");
    setAltText(asset.alt_text ?? "");
    const initial = initialAcceptFieldsFromAssets(
      [asset],
      tags,
      temporalTags.map((t) => t.id),
      contentRoleTagIds
    );
    setSolutionTagIds(initial.solutionTagIds);
    setSuggestedSolutionTagIds(initial.solutionTagIds);
    setTemporalTagId(initial.temporalTagId);
    setContentRoleIds(initial.contentRoleIds);
  }, [asset?.id, tags, temporalTags, contentRoleTagIds]);

  useEffect(() => {
    decideScrollEl?.scrollTo({ top: 0 });
  }, [asset?.id, decideScrollEl]);

  const previewSrc = asset?.url || asset?.thumbnail_url || "";

  useEffect(() => {
    setPreviewLoaded(false);
  }, [previewSrc, asset?.id]);

  useEffect(() => {
    setFlashKind(null);
  }, [asset?.id]);

  useLayoutEffect(() => {
    if (!open || !decideScrollEl) {
      setMoreBelow(false);
      return;
    }
    const el = decideScrollEl;
    const sync = () =>
      setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", sync);
      ro.disconnect();
    };
  }, [open, decideScrollEl, asset?.id, tags.length, contentRoleTags.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (busy || localBusy) return;
      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        onIndexChange(index - 1);
      } else if (event.key === "ArrowRight" && index < assets.length - 1) {
        event.preventDefault();
        onIndexChange(index + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, assets.length, onIndexChange, busy, localBusy]);

  const lightboxImages = useMemo(
    () =>
      assets.map((a) => ({
        src: a.url || a.thumbnail_url || "",
        alt: a.alt_text || a.caption || `Asset ${a.id}`,
        caption: a.caption || undefined,
      })),
    [assets]
  );

  if (!assets.length) return null;

  const actions = getAvailableActions(asset?.verification_status);
  const { needsSolutionTag, needsTemporalTag } = getAcceptReadiness({
    solutionTagIds,
    temporalTagId,
    temporalTagCount: temporalTags.length,
  });
  const requireAcceptFields = Boolean(actions.accept);
  const missingSolution = requireAcceptFields && needsSolutionTag;
  const missingTemporal = requireAcceptFields && needsTemporalTag;
  const missingBoardUser = !boardUserAvailable;
  const writesBlocked = Boolean(busy) || localBusy || missingBoardUser;
  const navBlocked = Boolean(busy) || localBusy;
  const acceptDisabled = writesBlocked || missingSolution || missingTemporal;
  const otherDisabled = writesBlocked;
  const src = previewSrc;
  const statusLabel = asset?.verification_status?.replaceAll("_", " ") ?? "Asset";
  const flash = flashKind ? ACTION_FLASH[flashKind] : null;
  const FlashIcon = flash?.Icon;
  const recommended = asset?.recommended_disposition;
  const writingLabel =
    flashKind === "accept"
      ? "Accepting…"
      : flashKind === "reject"
        ? "Rejecting…"
        : flashKind === "skip"
          ? "Skipping…"
          : flashKind === "reprocess"
            ? "Reprocessing…"
            : navBlocked
              ? "Working…"
              : null;

  const disableHints: string[] = [];
  if (!(busy || localBusy)) {
    if (missingBoardUser) {
      disableHints.push("A board user is required before you can accept, reject, or skip.");
    } else if (missingTemporal) {
      disableHints.push("Select a temporal phase to enable Accept.");
    } else if (missingSolution) {
      disableHints.push("Select at least one solution tag to enable Accept.");
    }
  }

  const runWithFlash = async (kind: FlashKind, work: () => Promise<void>) => {
    setFlashKind(kind);
    setLocalBusy(true);
    try {
      await delay(reduceMotion ? 160 : 520);
      await work();
    } catch {
      setFlashKind(null);
    } finally {
      setLocalBusy(false);
    }
  };

  return (
    <LightboxProvider images={lightboxImages} allowZoom onIndexChange={onIndexChange}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton
          closeButtonClassName="right-3 top-3 z-30"
          className="flex h-[min(92vh,880px)] w-[min(96vw,1280px)] max-w-[1280px] flex-col gap-0 overflow-hidden p-0 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 sm:rounded-xl"
        >
          <div className="grid min-h-0 flex-1 grid-rows-[minmax(12rem,38vh)_minmax(0,1fr)] md:grid-rows-1 md:grid-cols-[minmax(0,1fr)_minmax(28rem,40rem)]">
            {/* Left — image uses the open horizontal space */}
            <div className="relative min-h-0 bg-muted md:border-r md:border-border">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={asset?.id ?? "empty"}
                  className="absolute inset-0"
                  initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -8 }}
                  transition={{ duration: reduceMotion ? 0.1 : 0.18, ease: "easeOut" }}
                >
                  {src ? (
                    <LightboxImageTrigger
                      index={index}
                      className="absolute inset-0 block cursor-zoom-in"
                    >
                      <div className="relative flex h-full w-full items-center justify-center p-3">
                        {!previewLoaded && (
                          <div
                            className="absolute inset-0 animate-pulse bg-muted-foreground/10"
                            aria-hidden
                          />
                        )}
                        <img
                          src={src}
                          alt={asset?.alt_text || asset?.caption || `Asset ${asset?.id}`}
                          className={`max-h-full max-w-full object-contain transition-opacity duration-200 ${
                            previewLoaded ? "opacity-100" : "opacity-0"
                          }`}
                          onLoad={() => setPreviewLoaded(true)}
                          onError={() => setPreviewLoaded(true)}
                        />
                      </div>
                    </LightboxImageTrigger>
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      No preview
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-[1] flex items-center justify-between px-2">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="pointer-events-auto h-10 w-10 rounded-full border border-border/80 bg-card/95 text-foreground shadow-md backdrop-blur-sm hover:bg-card disabled:opacity-40"
                  disabled={index <= 0 || navBlocked}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onIndexChange(index - 1)}
                  aria-label="Previous asset"
                >
                  <ChevronLeft className="h-5 w-5" strokeWidth={2.5} />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="pointer-events-auto h-10 w-10 rounded-full border border-border/80 bg-card/95 text-foreground shadow-md backdrop-blur-sm hover:bg-card disabled:opacity-40"
                  disabled={index >= assets.length - 1 || navBlocked}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onIndexChange(index + 1)}
                  aria-label="Next asset"
                >
                  <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
                </Button>
              </div>

              {/* Observations (top-right) + disposition suggestion (bottom-left) — task Promote pattern */}
              {asset &&
                ((asset.ai_observations?.length ?? 0) > 0 ||
                  (asset.visibility_warnings?.length ?? 0) > 0) && (
                <div className="pointer-events-none absolute inset-0 z-[15] overflow-hidden">
                  <AnimatePresence>
                    <motion.div
                      key={`ai-obs-${asset.id}`}
                      initial={
                        reduceMotion
                          ? { opacity: 0 }
                          : { opacity: 0, y: 16, scale: 0.95 }
                      }
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={
                        reduceMotion
                          ? { opacity: 0 }
                          : { opacity: 0, y: 16, scale: 0.95 }
                      }
                      transition={
                        reduceMotion
                          ? { duration: 0.12 }
                          : { type: "spring", stiffness: 300, damping: 25 }
                      }
                      className="absolute top-4 right-4 max-h-[calc(100%-2rem)] max-w-[min(280px,46%)] space-y-2.5 overflow-y-auto [scrollbar-width:thin]"
                    >
                      <AiObservationsCard
                        items={asset.ai_observations ?? []}
                        className="backdrop-blur-md"
                      />
                      <AiWarningsCard
                        items={asset.visibility_warnings ?? []}
                        delay={(asset.ai_observations?.length ?? 0) > 0 ? 0.15 : 0}
                        className="backdrop-blur-md"
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}

              {asset && (
                <div className="pointer-events-none absolute inset-0 z-[15] overflow-hidden">
                  <AnimatePresence>
                    <motion.div
                      key={`ai-suggest-${asset.id}`}
                      initial={
                        reduceMotion
                          ? { opacity: 0 }
                          : { opacity: 0, y: 12, scale: 0.96 }
                      }
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={
                        reduceMotion
                          ? { opacity: 0 }
                          : { opacity: 0, y: 12, scale: 0.96 }
                      }
                      transition={
                        reduceMotion
                          ? { duration: 0.12 }
                          : { type: "spring", stiffness: 300, damping: 26 }
                      }
                      className="absolute bottom-4 right-4 max-h-[min(40%,16rem)] max-w-[min(320px,52%)] overflow-y-auto [scrollbar-width:thin]"
                    >
                      <PromoteSurfaceAiProposal asset={asset} variant="overlay" />
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}

              <AnimatePresence>
                {flash && FlashIcon && flashKind && (
                  <motion.div
                    key={flashKind}
                    role="status"
                    aria-live="polite"
                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.88 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: reduceMotion ? 0.12 : 0.22, ease: "easeOut" }}
                    className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/55"
                  >
                    <span
                      className={`flex h-16 w-16 items-center justify-center rounded-full ${flash.iconWrap}`}
                    >
                      <FlashIcon
                        className="h-9 w-9"
                        strokeWidth={flashKind === "accept" ? 2.75 : 2.25}
                        aria-hidden
                      />
                    </span>
                    <span className={`text-sm font-semibold tracking-wide ${flash.labelClass}`}>
                      {flash.label}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right — all decision UI visible; scrolls only if viewport is short */}
            <div className="flex min-h-0 flex-col bg-background">
              <DialogHeader className="shrink-0 space-y-1 border-b border-border px-5 py-3.5 pr-14 text-left">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DialogTitle className="cursor-default text-base leading-snug line-clamp-2">
                      {asset?.caption || `Asset ${asset?.id ?? ""}`}
                    </DialogTitle>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-sm text-left">
                    {asset?.caption || `Asset ${asset?.id ?? ""}`}
                  </TooltipContent>
                </Tooltip>
                <DialogDescription className="flex items-center gap-2 text-xs">
                  <span className="rounded-md bg-muted px-1.5 py-0.5 font-medium uppercase tracking-wide text-foreground">
                    {statusLabel}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {index + 1} / {assets.length}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="relative min-h-0 flex-1">
                <div
                  ref={setDecideScrollEl}
                  className="h-full space-y-4 overflow-y-auto px-4 pb-9 pt-4 [scrollbar-width:thin]"
                >
                  {asset && (
                    <div className="space-y-4">
                      <PromoteSurfaceAcceptFields
                        tags={tags}
                        temporalTags={temporalTags}
                        contentRoleTags={contentRoleTags}
                        solutionTagIds={solutionTagIds}
                        onSolutionTagIdsChange={setSolutionTagIds}
                        suggestedSolutionTagIds={suggestedSolutionTagIds}
                        temporalTagId={temporalTagId}
                        onTemporalTagIdChange={setTemporalTagId}
                        contentRoleIds={contentRoleIds}
                        onContentRoleIdsChange={setContentRoleIds}
                        disabled={navBlocked || !actions.accept}
                        showInlineHints={actions.accept}
                      />

                      <section className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                            Caption
                          </label>
                          <Input
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            disabled={navBlocked || !actions.accept}
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                            Alt text
                          </label>
                          <Input
                            value={altText}
                            onChange={(e) => setAltText(e.target.value)}
                            disabled={navBlocked || !actions.accept}
                          />
                        </div>
                      </section>
                    </div>
                  )}

                  {asset?.actioned_by_name && (
                    <p className="text-xs text-muted-foreground">
                      Last actioned by {asset.actioned_by_name}
                      {asset.actioned_at
                        ? ` · ${new Date(asset.actioned_at).toLocaleString()}`
                        : null}
                    </p>
                  )}
                </div>

                {moreBelow && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] flex justify-center bg-gradient-to-t from-background from-30% to-transparent pb-1 pt-10">
                    <button
                      type="button"
                      className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm hover:text-foreground"
                      aria-label="More fields below"
                      onClick={() =>
                        decideScrollEl?.scrollBy({
                          top: Math.max(120, Math.floor((decideScrollEl?.clientHeight ?? 240) * 0.45)),
                          behavior: reduceMotion ? "auto" : "smooth",
                        })
                      }
                    >
                      <ChevronDown className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                    </button>
                  </div>
                )}
              </div>

              {asset && (
                <div className="shrink-0 space-y-2 border-t border-border px-4 py-3">
                  {disableHints.length > 0 && (
                    <div
                      className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
                      role="status"
                    >
                      {disableHints.map((hint) => (
                        <p key={hint}>{hint}</p>
                      ))}
                    </div>
                  )}
                  <DialogFooter className="flex-row flex-nowrap items-center justify-stretch gap-2 border-0 p-0 sm:space-x-0">
                    {actions.accept && (
                      <Button
                        disabled={acceptDisabled}
                        className="min-w-0 flex-1"
                        variant={recommended === "accept" ? "default" : "secondary"}
                        title={
                          missingTemporal
                            ? "Select a temporal phase"
                            : missingSolution
                              ? "Select at least one solution tag"
                              : missingBoardUser
                                ? "Board user required"
                                : writingLabel ?? undefined
                        }
                        onClick={() =>
                          void runWithFlash("accept", () =>
                            Promise.resolve(
                              onAccept(asset, solutionTagIds, {
                                caption: caption || null,
                                alt_text: altText || null,
                                temporalTagId,
                                contentRoleIds,
                              })
                            )
                          )
                        }
                      >
                        {flashKind === "accept" && localBusy ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            Accepting…
                          </>
                        ) : (
                          "Accept"
                        )}
                      </Button>
                    )}
                    {actions.reject && (
                      <Button
                        variant={recommended === "reject" ? "default" : "secondary"}
                        disabled={otherDisabled}
                        className="min-w-0 flex-1"
                        title={
                          missingBoardUser
                            ? "Board user required"
                            : writingLabel ?? undefined
                        }
                        onClick={() =>
                          void runWithFlash("reject", () =>
                            Promise.resolve(onAction("reject", asset))
                          )
                        }
                      >
                        {flashKind === "reject" && localBusy ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            Rejecting…
                          </>
                        ) : (
                          "Reject"
                        )}
                      </Button>
                    )}
                    {actions.skip && (
                      <Button
                        variant={recommended === "skip" ? "default" : "secondary"}
                        disabled={otherDisabled}
                        className="min-w-0 flex-1"
                        title={
                          missingBoardUser
                            ? "Board user required"
                            : writingLabel ?? undefined
                        }
                        onClick={() =>
                          void runWithFlash("skip", () =>
                            Promise.resolve(onAction("skip", asset))
                          )
                        }
                      >
                        {flashKind === "skip" && localBusy ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            Skipping…
                          </>
                        ) : (
                          skipActionLabel(asset?.verification_status)
                        )}
                      </Button>
                    )}
                    {actions.reprocess && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={navBlocked}
                            className="shrink-0"
                            aria-label={
                              flashKind === "reprocess" && localBusy
                                ? "Reprocessing"
                                : "Reprocess"
                            }
                            onClick={() =>
                              void runWithFlash("reprocess", () =>
                                Promise.resolve(onAction("reprocess", asset))
                              )
                            }
                          >
                            {flashKind === "reprocess" && localBusy ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {writingLabel ?? "Reprocess AI suggestion"}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </DialogFooter>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </LightboxProvider>
  );
}
